import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import { HttpClient, HttpErrorResponse, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { FooterComponent } from './components/footer/footer.component';
import { HeaderComponent } from './components/header/header.component';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { HomeComponent } from './pages/home/home.component';
import { RaygunErrorHandler } from './app.raygun.setup';
import { CoreModule } from './core/core.module';
import { LoginComponent } from './pages/login/login.component';
import { AboutUsComponent } from './components/about-us/about-us.component';
import { InboxListComponent } from './components/inbox-list/inbox-list.component';
import { NavBarComponent } from './components/nav-bar/nav-bar.component';
import { httpInterceptorProviders } from './http-interceptors';
import { MfaComponent } from './pages/login/mfa/mfa.component';
import { MfaHelpDialogComponent } from './pages/login/mfa/mfa-help-dialog.component';
import { ForgotPasswordComponent } from './pages/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './pages/reset-password/reset-password.component';
import { ViewMessageComponent } from './pages/view-message/view-message.component';
import { ActivityListComponent } from './components/activity-list/activity-list.component';
import { MessagesComponent } from './pages/messages/messages.component';
import { MessageListComponent } from './components/message-list/message-list.component';
import { MattersListComponent } from './components/matters-list/matters-list.component';
import { MessageComposeComponent } from './pages/message-compose/message-compose.component';
import { ChangePasswordComponent } from './pages/change-password/change-password.component';
import { PrivacyPolicyComponent } from './pages/privacy-policy/privacy-policy.component';
import { ViewMatterComponent } from './pages/view-matter/view-matter.component';
import { PasswordChangedSuccessDialogComponent } from './components/password-changed-success-dialog/password-changed-success-dialog.component';
import { SecurityPrivacyCookiePolicyComponent } from './components/security-privacy-cookie-policy/security-privacy-cookie-policy.component';
import { TermsAndConditionsComponent } from './components/terms-and-conditions/terms-and-conditions.component';
import { MattersPageComponent } from './pages/matters-page/matters-page.component';
import { MattersCardComponent } from './components/matters-card/matters-card.component';
import { DocumentListComponent } from './components/document-list/document-list.component';
import { DocumentDetailsDialogComponent } from './components/document-details-dialog/document-details-dialog.component';
import { ByteFormatPipe } from "./core/pipes/byte-format.pipe";
import { MfaBannerComponent } from './components/mfa-banner/mfa-banner.component';
import { SessionTimeoutDialogComponent } from './components/session-timeout-dialog/session-timeout-dialog.component';
import { SessionTimeoutService } from './core/services/session-timeout.service';
import { Overlay, OverlayModule, ScrollStrategy } from '@angular/cdk/overlay';
import { MAT_MENU_SCROLL_STRATEGY } from '@angular/material/menu';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { LayoutModule } from '@angular/cdk/layout';
import { DocumentUploadComponent } from './components/document-upload/document-upload.component';
import { DocumentSelectComponent } from './components/document-select/document-select.component';
import { MatSelectModule } from "@angular/material/select";
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDateFnsModule } from '@angular/material-date-fns-adapter';
import { MatIconModule } from '@angular/material/icon';
import { MatStepperModule } from '@angular/material/stepper';
import { FormsModule } from '@angular/forms';
import { SentListComponent } from './components/sent-list/sent-list.component';
import { HiddenCopyDisplayComponent } from './core/components/hidden-copy-display/hidden-copy-display.component';
import { StartPageComponent } from './assessment-app/pages/start-page/start-page.component';
import { ApplicantDetailsComponent } from './assessment-app/pages/applicant-details/applicant-details.component';
import { AddressComponent } from './assessment-app/components/address/address.component';
import { MainComponent } from './assessment-app/pages/main/main.component';
import { ProgressBarComponent } from './assessment-app/components/progress-bar/progress-bar.component';
import { ButtonBarComponent } from './assessment-app/components/button-bar/button-bar.component';
import { DeleteFormDialogComponent } from './assessment-app/components/delete-form-dialog/delete-form-dialog.component';
import { ErrorMessageComponent } from './assessment-app/components/error-message/error-message.component';
import { SaveFormDialogComponent } from './assessment-app/components/save-form-dialog/save-form-dialog.component';
import { SubmitFormDialogComponent } from './assessment-app/components/submit-form-dialog/submit-form-dialog.component';
import { EmployerDetailsComponent } from './assessment-app/pages/employer-details/employer-details.component';
import { OtherFamilyComponent } from './assessment-app/pages/other-family/other-family.component';
import { HealthCharacterComponent } from './assessment-app/pages/health-character/health-character.component';
import { FinishComponent } from './assessment-app/pages/finish/finish.component';
import { GeneralEnquiryComponent } from './assessment-app/pages/general-enquiry/general-enquiry.component';
import { BusinessEnquiryComponent } from './assessment-app/pages/business-enquiry/business-enquiry.component';
import { EducationComponent } from './assessment-app/pages/education/education.component';
import { BusinessSkillsComponent } from './assessment-app/pages/business-skills/business-skills.component';
import { DecisionComponent } from './assessment-app/pages/decision/decision.component';
import { A11yModule } from "@angular/cdk/a11y";
import { StartPageNoticesComponent } from './assessment-app/components/start-page-notices/start-page-notices.component';
import { LoadFormDialogComponent } from './assessment-app/components/load-form-dialog/load-form-dialog.component';
import { QualificationDialogComponent } from './assessment-app/components/qualification-dialog/qualification-dialog.component';
import { FormFieldsModule } from './assessment-app/components/form-fields/form-fields.module';
import { OwnershipDialogComponent } from './assessment-app/components/ownership-dialog/ownership-dialog.component';
import { PersonDialogComponent } from './assessment-app/components/person-dialog/person-dialog.component';
import { FamilyMembersComponent } from './assessment-app/components/family-members/family-members.component';
import { NonIncludedChildrenComponent } from './assessment-app/components/non-included-children/non-included-children.component';
import { OccupationDetailsComponent } from './assessment-app/pages/occupation-details/occupation-details.component';
import { EmploymentDialogComponent } from './assessment-app/components/employment-dialog/employment-dialog.component';
import { EmployerSponsorComponent } from './assessment-app/pages/employer-sponsor/employer-sponsor.component';
import { BusinessInvestmentsComponent } from './assessment-app/pages/business-investments/business-investments.component';
import { PositionsComponent } from './assessment-app/pages/positions/positions.component';
import { HealthConditionDialogComponent } from './assessment-app/components/health-condition-dialog/health-condition-dialog.component';
import { CrimeDetailsDialogComponent } from './assessment-app/components/crime-details-dialog/crime-details-dialog.component';
import { RefusedVisaDialogComponent } from './assessment-app/components/refused-visa-dialog/refused-visa-dialog.component';
import { CancelledVisaDialogComponent } from './assessment-app/components/cancelled-visa-dialog/cancelled-visa-dialog.component';
import { RelationshipPartnerComponent } from './assessment-app/pages/relationship-partner/relationship-partner.component';
import { DataProtectionComponent } from './assessment-app/components/data-protection/data-protection.component';
import { FeedbackComponent } from './assessment-app/components/feedback/feedback.component';
import { ThankYouComponent } from './assessment-app/pages/thank-you/thank-you.component';
import { FileUploadDialogComponent } from './assessment-app/components/file-upload-dialog/file-upload-dialog.component';
import { BusinessEnquiryFormComponent } from './assessment-app/components/business-enquiry-form/business-enquiry-form.component';
import { BusinessService } from './core/services/business.service';
import { firstValueFrom, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { PositionDialogComponent } from './assessment-app/components/position-dialog/position-dialog.component';
import { SponsorDetailsComponent } from './assessment-app/pages/sponsor-details/sponsor-details.component';
import { enAU } from 'date-fns/locale';
import { NominationsCardComponent } from './components/nominations-card/nominations-card.component';
import { NominationDetailComponent } from './components/nomination-detail/nomination-detail.component';

function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './i18n/', '.json');
}

export function menuScrollStrategyFactory(overlay: Overlay): () => ScrollStrategy {
  return () => overlay.scrollStrategies.close();
}

function initializeApp(sessionTimeoutService: SessionTimeoutService, businessService: BusinessService, router: Router) {
  return async () => {

    sessionTimeoutService.startSessionCheck();

    // Handle Subdomain Validation
    // if 200 - check if response.exists is true
    // If its false, replace subdomain in current url with response.redirectSubdomain and reload page
    // catch 404 error, and route to error page

    try {
      const response = await firstValueFrom(businessService.ValidateSubdomain());
      if (!response.exists && response.redirectSubdomain) {
        const currentHostname = window.location.hostname;
        const newHostname = currentHostname.replace(currentHostname.split('.')[0], response.redirectSubdomain);
        const newUrl = window.location.href.replace(currentHostname, newHostname);
        window.location.href = newUrl;
      }
      return;
    }
    catch (error: any) {
      if (error.status === 404) {
        return router.navigate(['/404']);
      }
      return throwError(() => error);
    }
  };
}

@NgModule({
  declarations: [
    FooterComponent,
    HeaderComponent,
    HomeComponent,
    AppComponent,
    LoginComponent,
    AboutUsComponent,
    InboxListComponent,
    ActivityListComponent,
    NominationsCardComponent,
    NominationDetailComponent,
    MattersListComponent,
    NavBarComponent,
    MfaComponent,
    MfaHelpDialogComponent,
    ForgotPasswordComponent,
    ResetPasswordComponent,
    ViewMessageComponent,
    MessagesComponent,
    MessageListComponent,
    MessageComposeComponent,
    ViewMatterComponent,
    MattersPageComponent,
    MattersCardComponent,
    ChangePasswordComponent,
    PrivacyPolicyComponent,
    PasswordChangedSuccessDialogComponent,
    SecurityPrivacyCookiePolicyComponent,
    TermsAndConditionsComponent,
    DocumentListComponent,
    DocumentDetailsDialogComponent,
    MfaBannerComponent,
    SessionTimeoutDialogComponent,
    DocumentUploadComponent,
    DocumentSelectComponent,
    SentListComponent,
    HiddenCopyDisplayComponent,
    StartPageComponent,
    ApplicantDetailsComponent,
    AddressComponent,
    MainComponent,
    ProgressBarComponent,
    ButtonBarComponent,
    DeleteFormDialogComponent,
    ErrorMessageComponent,
    SaveFormDialogComponent,
    SubmitFormDialogComponent,
    EmployerDetailsComponent,
    EmployerSponsorComponent,
    OtherFamilyComponent,
    HealthCharacterComponent,
    FinishComponent,
    GeneralEnquiryComponent,
    BusinessEnquiryComponent,
    EducationComponent,
    BusinessSkillsComponent,
    DecisionComponent,
    StartPageNoticesComponent,
    LoadFormDialogComponent,
    DecisionComponent,
    QualificationDialogComponent,
    BusinessInvestmentsComponent,
    OwnershipDialogComponent,
    PersonDialogComponent,
    FamilyMembersComponent,
    NonIncludedChildrenComponent,
    OccupationDetailsComponent,
    EmploymentDialogComponent,
    PositionsComponent,
    PositionDialogComponent,
    SponsorDetailsComponent,
    HealthConditionDialogComponent,
    CrimeDetailsDialogComponent,
    RefusedVisaDialogComponent,
    CancelledVisaDialogComponent,
    RelationshipPartnerComponent,
    DataProtectionComponent,
    FeedbackComponent,
    ThankYouComponent,
    FileUploadDialogComponent,
    BusinessEnquiryFormComponent,
    HiddenCopyDisplayComponent,
    StartPageComponent,
    NotFoundComponent
  ],
  imports: [
    CommonModule,
    BrowserModule,
    AppRoutingModule,
    CoreModule,
    FormsModule,
    DragDropModule,
    LayoutModule,
    MatProgressBarModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatRadioModule,
    MatDatepickerModule,
    MatDateFnsModule,
    MatIconModule,
    MatStepperModule,
    FormFieldsModule,
    TranslateModule.forRoot({
        loader: {
            provide: TranslateLoader,
            useFactory: HttpLoaderFactory,
            deps: [HttpClient],
        },
        defaultLanguage: 'en',
    }),
    ByteFormatPipe,
    ScrollingModule,
    OverlayModule,
    A11yModule
],
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    httpInterceptorProviders,
    { provide: ErrorHandler, useClass: RaygunErrorHandler },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [SessionTimeoutService, BusinessService, Router],
      multi: true
    },
    {
      provide: MAT_MENU_SCROLL_STRATEGY,
      useFactory: menuScrollStrategyFactory,
      deps: [Overlay]
    },
    {provide: MAT_DATE_LOCALE, useValue: enAU}
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
